import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, EmptyState, Spinner } from "@/components/ui/card";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

import { PanelPhotos } from "./PanelPhotos";
import { usePropertyContext } from "./PropertyDetailPage";

interface BreakerRow {
  id: string;
  number: string;
  amperage: number;
  description?: string;
  room?: string | null;
}

export function PanelsTab() {
  const { property } = usePropertyContext();
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const key = ["property-tree", property.id];
  const tree = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/{id}/tree/", { params: { path: { id: property.id } } }),
      ),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const onError = (e: unknown) => toast.error(errorMessage(e, t("common.error")));

  const [panelDialog, setPanelDialog] = useState<{
    id?: string;
    name: string;
    location: string;
  } | null>(null);
  const [breakerDialog, setBreakerDialog] = useState<{
    panelId: string;
    breaker?: BreakerRow;
  } | null>(null);
  const [del, setDel] = useState<{ kind: "panel" | "breaker"; id: string; label: string } | null>(
    null,
  );

  const savePanel = useMutation({
    mutationFn: async (p: { id?: string; name: string; location: string }) =>
      p.id
        ? unwrap(
            await api.PATCH("/api/panels/{id}/", {
              params: { path: { id: p.id } },
              body: { name: p.name, location: p.location },
            }),
          )
        : unwrap(
            await api.POST("/api/panels/", {
              body: { property: property.id, name: p.name, location: p.location },
            }),
          ),
    onSuccess: () => {
      setPanelDialog(null);
      void refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: async (d: { kind: "panel" | "breaker"; id: string }) => {
      const res =
        d.kind === "panel"
          ? await api.DELETE("/api/panels/{id}/", { params: { path: { id: d.id } } })
          : await api.DELETE("/api/breakers/{id}/", { params: { path: { id: d.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setDel(null);
      void refresh();
    },
    onError,
  });

  if (tree.isLoading) return <Spinner className="m-8 size-8" />;
  const panels = tree.data?.panels ?? [];
  const rooms = tree.data?.rooms ?? [];
  const roomName = (id?: string | null) => rooms.find((r) => r.id === id)?.name ?? "";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button onClick={() => setPanelDialog({ name: t("panels.default_name"), location: "" })}>
          <Plus className="size-4" /> {t("panels.add")}
        </Button>
      </div>
      {panels.length === 0 && <EmptyState>{t("panels.empty")}</EmptyState>}

      {panels.map((panel) => (
        <Card key={panel.id}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <h3 className="text-lg font-bold">{panel.name}</h3>
              {panel.location && (
                <div className="text-sm text-muted-foreground">{panel.location}</div>
              )}
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.edit")}
                onClick={() =>
                  setPanelDialog({
                    id: panel.id,
                    name: panel.name ?? "",
                    location: panel.location ?? "",
                  })
                }
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.delete")}
                onClick={() => setDel({ kind: "panel", id: panel.id, label: panel.name ?? "" })}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
          <PanelPhotos propertyId={property.id} panelId={panel.id} />
          {panel.breakers.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">№</th>
                    <th className="px-3 py-2">A</th>
                    <th className="px-3 py-2">{t("panels.line")}</th>
                    <th className="px-3 py-2">{t("panels.room")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {panel.breakers.map((b) => (
                    <tr key={b.id} className="border-t border-border">
                      <td className="px-3 py-2 font-semibold">{b.number}</td>
                      <td className="px-3 py-2">{b.amperage}</td>
                      <td className="px-3 py-2">{b.description}</td>
                      <td className="px-3 py-2">{roomName(b.room)}</td>
                      <td className="px-3 py-1 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.edit")}
                          onClick={() => setBreakerDialog({ panelId: panel.id, breaker: b })}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.delete")}
                          onClick={() =>
                            setDel({
                              kind: "breaker",
                              id: b.id,
                              label: `${b.number} (${b.amperage}A)`,
                            })
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setBreakerDialog({ panelId: panel.id })}
          >
            <Plus className="size-4" /> {t("panels.add_breaker")}
          </Button>
        </Card>
      ))}

      <Dialog
        open={!!panelDialog}
        onOpenChange={(o) => !o && setPanelDialog(null)}
        title={panelDialog?.id ? t("panels.edit") : t("panels.add")}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (panelDialog?.name.trim()) savePanel.mutate(panelDialog);
          }}
        >
          <Field label={t("panels.name")}>
            {(id) => (
              <Input
                id={id}
                value={panelDialog?.name ?? ""}
                onChange={(e) => setPanelDialog((p) => (p ? { ...p, name: e.target.value } : p))}
              />
            )}
          </Field>
          <Field label={t("panels.location")}>
            {(id) => (
              <Input
                id={id}
                value={panelDialog?.location ?? ""}
                onChange={(e) =>
                  setPanelDialog((p) => (p ? { ...p, location: e.target.value } : p))
                }
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPanelDialog(null)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={savePanel.isPending}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </Dialog>

      {breakerDialog && (
        <BreakerDialog
          key={breakerDialog.breaker?.id ?? "new"}
          panelId={breakerDialog.panelId}
          breaker={breakerDialog.breaker}
          rooms={rooms.map((r) => ({ id: r.id, name: r.name }))}
          onClose={() => setBreakerDialog(null)}
          onSaved={() => {
            setBreakerDialog(null);
            void refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t(del?.kind === "panel" ? "panels.delete" : "panels.delete_breaker")}
        message={t("struct.delete_message", { name: del?.label ?? "" })}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => del && remove.mutate(del)}
      />
    </div>
  );
}

function BreakerDialog({
  panelId,
  breaker,
  rooms,
  onClose,
  onSaved,
}: {
  panelId: string;
  breaker?: BreakerRow;
  rooms: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const [number, setNumber] = useState(breaker?.number ?? "");
  const [amperage, setAmperage] = useState(breaker ? String(breaker.amperage) : "16");
  const [description, setDescription] = useState(breaker?.description ?? "");
  const [room, setRoom] = useState(breaker?.room ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        number: number.trim(),
        amperage: Number(amperage),
        description,
        room: room || null,
      };
      return breaker
        ? unwrap(
            await api.PATCH("/api/breakers/{id}/", { params: { path: { id: breaker.id } }, body }),
          )
        : unwrap(await api.POST("/api/breakers/", { body: { panel: panelId, ...body } }));
    },
    onSuccess: onSaved,
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={breaker ? t("panels.edit_breaker") : t("panels.add_breaker")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (number.trim() && Number(amperage) > 0) save.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("panels.number")}>
            {(id) => <Input id={id} value={number} onChange={(e) => setNumber(e.target.value)} />}
          </Field>
          <Field label={t("panels.amperage")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={1}
                max={630}
                value={amperage}
                onChange={(e) => setAmperage(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={t("panels.line")} hint={t("panels.line_hint")}>
          {(id) => (
            <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />
          )}
        </Field>
        <Field label={t("panels.room")}>
          {(id) => (
            <Select id={id} value={room} onChange={(e) => setRoom(e.target.value)}>
              <option value="">—</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
